import _jsx from '@babel/runtime/helpers/jsx';
import _extends from '@babel/runtime/helpers/extends';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { forwardRef, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { useId } from '@andes/context';
import { VisuallyHidden } from '@andes/common';
import FeedbackPositive16 from '@andes/icons/FeedbackPositive16';
import warning from '@andes/common/build/utils/warning';
var _svg;
const IndeterminateIcon = () => _svg || (_svg = /*#__PURE__*/_jsx("svg", {
  "aria-hidden": "true",
  width: "16",
  height: "16",
  viewBox: "0 0 16 16",
  xmlns: "http://www.w3.org/2000/svg"
}, void 0, /*#__PURE__*/_jsx("rect", {
  width: "10",
  height: "1.6",
  x: "3",
  y: "7.2",
  fill: "currentColor"
})));
var IndeterminateIcon$1 = IndeterminateIcon;
const namespace = 'andes-checkbox';
const WARNING_SPREAD = "Props spreading will be deprecated in a future major version.\nIf you want to pass props to the <Checkbox> input, please use 'inputProps' prop instead.";
const LABEL_POSITION_LEFT = 'left';
const LABEL_POSITION_RIGHT = 'right';
const computeError = _ref => {
  let {
    error,
    modifier
  } = _ref;
  if (modifier === 'error') return true;
  return error;
};
var computeError$1 = computeError;
const computeIndeterminate = _ref => {
  let {
    indeterminate,
    type
  } = _ref;
  if (type === 'indeterminate') return true;
  return indeterminate;
};
var computeIndeterminate$1 = computeIndeterminate;
const computeLabelPosition = _ref => {
  let {
    inverted,
    labelPosition
  } = _ref;
  if (typeof inverted !== 'undefined') return inverted ? LABEL_POSITION_LEFT : LABEL_POSITION_RIGHT;
  return labelPosition || LABEL_POSITION_RIGHT;
};
var computeLabelPosition$1 = computeLabelPosition;
var _IndeterminateIcon, _FeedbackPositive;
const _excluded = ["checked", "className", "defaultChecked", "disabled", "error", "highlight", "id", "indeterminate", "inputProps", "inverted", "label", "labelPosition", "modifier", "onChange", "srHidden", "srLabel", "type"];
const Checkbox = /*#__PURE__*/function () {
  const Checkbox = /*#__PURE__*/forwardRef((props, ref) => {
    const {
        checked,
        className,
        defaultChecked,
        disabled = false,
        error = false,
        highlight = false,
        id,
        indeterminate = false,
        inputProps = {},
        inverted,
        label,
        labelPosition = 'right',
        modifier,
        onChange,
        srHidden = false,
        srLabel,
        type
      } = props,
      otherProps = _objectWithoutProperties(props, _excluded);
    const {
      current: isControlled
    } = useRef(typeof checked !== 'undefined');
    const [isChecked, setIsChecked] = useState(defaultChecked || checked);
    useEffect(() => {
      if (isControlled && checked !== undefined) {
        setIsChecked(checked);
      }
    }, [checked, isControlled]);
    useEffect(() => {
      warning(Object.keys(otherProps).length === 0, WARNING_SPREAD);
    }, []);
    const computedError = computeError$1({
      error,
      modifier
    });
    const computedIndeterminate = computeIndeterminate$1({
      indeterminate,
      type
    });
    const computedLabelPosition = computeLabelPosition$1({
      inverted,
      labelPosition
    });
    const componentId = useId(id);
    const srLabelId = "".concat(componentId, "-srLabel");
    const inputRef = useRef(null);
    const handleChange = event => {
      if (!isControlled) setIsChecked(event.target.checked);
      if (typeof onChange === 'function') onChange(event);
    };
    const checkboxClassnames = classNames(namespace, className, {
      ["".concat(namespace, "--error")]: computedError,
      ["".concat(namespace, "--highlight")]: highlight,
      ["".concat(namespace, "--label-left")]: computedLabelPosition === LABEL_POSITION_LEFT,
      ["".concat(namespace, "--indeterminate")]: computedIndeterminate && isChecked,
      ["".concat(namespace, "--disabled")]: disabled
    });
    const labelClassnames = classNames("".concat(namespace, "__label"), {
      ["".concat(namespace, "__label-text")]: label
    });
    const inputAttributes = _objectSpread(_objectSpread({}, otherProps), inputProps);
    const Wrapper = label ? 'label' : 'div';
    useEffect(() => {
      if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
        inputRef.current.indeterminate = computedIndeterminate && isChecked || false;
      }
    }, [inputRef, computedIndeterminate, isChecked]);
    return /*#__PURE__*/React.createElement(Wrapper, {
      className: checkboxClassnames,
      ref: ref
    }, /*#__PURE__*/_jsx("span", {
      className: "".concat(namespace, "__checkbox")
    }, void 0, /*#__PURE__*/React.createElement("input", _extends({}, inputAttributes, {
      ref: inputRef,
      checked: checked,
      className: "".concat(namespace, "__input"),
      defaultChecked: defaultChecked,
      disabled: disabled,
      id: componentId,
      onChange: handleChange,
      type: "checkbox"
    }, srLabel ? {
      'aria-labelledby': srLabelId
    } : {})), /*#__PURE__*/_jsx("span", {
      className: "".concat(namespace, "__icon")
    }, void 0, computedIndeterminate && isChecked ? _IndeterminateIcon || (_IndeterminateIcon = /*#__PURE__*/_jsx(IndeterminateIcon$1, {})) : _FeedbackPositive || (_FeedbackPositive = /*#__PURE__*/_jsx(FeedbackPositive16, {
      color: "currentColor"
    })))), label && /*#__PURE__*/_jsx("span", {
      "aria-hidden": !!srLabel || undefined,
      className: labelClassnames
    }, void 0, label), srLabel && /*#__PURE__*/_jsx(VisuallyHidden, {
      id: srLabelId
    }, void 0, srLabel));
  });
  Checkbox.displayName = 'Checkbox';
  return Checkbox;
}();
var Checkbox$1 = Checkbox;
export { Checkbox$1 as Checkbox, Checkbox$1 as default };
